import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';

import { useAuth } from '../hooks/use-auth';

const navItems = [
  { to: '/overview', label: 'Overview' },
  { to: '/clients', label: 'Clients' },
  { to: '/projects', label: 'Projects' },
  { to: '/invoices', label: 'Invoices' },
  { to: '/reconciliation', label: 'Reconciliation' },
  { to: '/usage', label: 'Usage' },
  { to: '/settings', label: 'Settings' },
  { to: '/integrations', label: 'Integrations' },
  { to: '/account', label: 'Account' },
];

export function AppShell() {
  const { user, doLogout } = useAuth();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    const onFeedback = (event: Event) => {
      const custom = event as CustomEvent<{ type: 'success' | 'error'; message: string }>;
      if (!custom.detail?.message) {
        return;
      }
      setFeedback(custom.detail);
    };

    window.addEventListener('api-feedback', onFeedback);
    return () => {
      window.removeEventListener('api-feedback', onFeedback);
    };
  }, []);

  useEffect(() => {
    if (!feedback) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setFeedback(null);
    }, 2800);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [feedback]);

  const toastClassName = useMemo(() => {
    if (!feedback) {
      return '';
    }

    return feedback.type === 'success'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
      : 'border-rose-300 bg-rose-50 text-rose-800';
  }, [feedback]);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="mx-auto flex min-h-screen w-full max-w-7xl gap-4 p-4 md:p-6">
        <aside className="w-full max-w-xs rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-6">
            <p className="text-lg font-semibold">Chicross Studio</p>
            <p className="text-sm text-slate-500">{user?.display_name ?? user?.email}</p>
          </div>

          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2 text-sm ${isActive ? 'bg-brand-100 text-brand-700' : 'text-slate-700 hover:bg-slate-100'}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <button
            type="button"
            onClick={async () => {
              await doLogout();
              navigate('/login');
            }}
            className="mt-6 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
          >
            Sign out
          </button>
        </aside>

        <main className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {feedback ? (
        <div className="pointer-events-none fixed inset-x-0 top-4 z-50 flex justify-center px-4">
          <div className={`rounded-md border px-4 py-2 text-sm shadow ${toastClassName}`}>{feedback.message}</div>
        </div>
      ) : null}
    </div>
  );
}
